import { Validations } from '@/validations.js';
import { Utils } from '@/utils.js';
import { METERS_TO_FEET, FEET_TO_METERS } from './store-state.js';

export const getters = {
    /*
	------------------------------------------------------------------------
	Flow control / step status getters
	------------------------------------------------------------------------
	*/
    getCurrentStepName() {
        const item = this.steps.find((step) => step.status === this.statusFlags.CURRENT);
        if (item) {
            return item.name;
        }
        return null;
    },
    getRoomTypes() {
        return this.roomtypes;
    },
    stepStart() {
        return this.user.currentStepName === 'Start';
    },
    stepProject() {
        return this.user.currentStepName === 'Project';
    },
    stepRoom() {
        return this.user.currentStepName === 'Room';
    },
    stepProduct() {
        return this.user.currentStepName === 'Product';
    },
    stepReport() {
        return this.user.currentStepName === 'Report';
    },
    getCurrentStepIndex: function () {
        return this.steps[this.user.currentStepIndex];
    },

    /*
	------------------------------------------------------------------------
	Product getters
	------------------------------------------------------------------------
	*/

    //
    // Product quantity getter.
    // Returns a function that calculates the effective quantity for a product.
    // Priority: manualUnits > calculation.unitsUsed > calculation.calculatedUnits > 1
    //
    getProductQuantity() {
        return (product) => {
            if (!product) return 1;
            return product.manualUnits ?? product.calculation?.unitsUsed ?? product.calculation?.calculatedUnits ?? 1;
        };
    },

    //
    // Get product by ID.
    // Returns the product object if found, null otherwise.
    //
    getProductById() {
        return (id) => {
            if (!id || !this.user.products || !Array.isArray(this.user.products)) {
                return null;
            }
            return this.user.products.find((p) => p.id === id) || null;
        };
    },

    //
    // Get product name by ID.
    // Returns formatted product name or fallback string.
    // Uses getProductById internally.
    //
    getProductName() {
        return (id) => {
            const product = this.getProductById(id);
            if (!product) {
                return 'Product';
            }
            return product.data?.name || `Product ${product.product || ''}`;
        };
    },

    //
    // Get primary product (first product in array).
    // Returns the product object or null if no products exist.
    //
    getPrimaryProduct() {
        if (!this.user.products || this.user.products.length === 0) {
            return null;
        }
        return this.user.products[0];
    },

    //
    // Check if a product is the primary product (first in array).
    // Returns a function that takes a productId and returns boolean.
    // Uses getPrimaryProduct internally.
    //
    isPrimaryProduct() {
        return (productId) => {
            const primary = this.getPrimaryProduct;
            return primary !== null && primary.id === productId;
        };
    },

    //
    // Check if user has any products.
    //
    hasProducts() {
        return this.user.products && this.user.products.length > 0;
    },

    //
    // Calculate total product count - returns object with distinctProducts and totalUnits
    //
    totalProductCount() {
        if (!this.user.products || this.user.products.length === 0) {
            return { distinctProducts: 0, totalUnits: 0 };
        }

        let distinctProductCount = 0;
        let totalUnits = 0;

        this.user.products.forEach((product) => {
            distinctProductCount++;
            const quantity = this.getProductQuantity(product);
            totalUnits += quantity;
        });

        return { distinctProducts: distinctProductCount, totalUnits: totalUnits };
    },

    //
    // Active product index - returns array index of the currently active product
    // Falls back to 0 if no activeProductId is set or product not found
    //
    activeProductIndex() {
        if (!this.user.activeProductId) {
            // No active product ID set, default to first product (index 0)
            return 0;
        }
        const index = this.user.products.findIndex((p) => p.id === this.user.activeProductId);
        // Return found index, or 0 if not found
        return index !== -1 ? index : 0;
    },
    //
    // Current product - returns the active product with enhanced properties
    // Returns enhanced product object with resolved size/variant properties for easy access
    //
    currentProduct() {
        const product = this.user.products[this.activeProductIndex];
        if (!product || !product.data) return null;

        // Resolve size and variant data from product catalog
        const sizeData = product.data.sizes?.items?.[product.size];
        const variantData = product.size_variant ? product.data.sizes?.variants?.all?.[product.size_variant] : null;

        return {
            ...product, // Original: id, product, size, size_variant, data, dataExtended, etc.

            // Flat resolved values for easy access (no deep property paths needed)
            sizeLabel: sizeData?.l || '',
            sizeWidth: sizeData?.w || 0,
            sizeHeight: variantData?.h || sizeData?.h || 0, // Variant takes precedence
            sizeDepth: sizeData?.d || 0,
            sizeId: sizeData?.id || '',

            variantLabel: variantData?.l || '',
            variantHeight: variantData?.h || 0,

            metricSabins: variantData?.metric_sabins || sizeData?.metric_sabins || '',

            // Alias for dataExtended (can contain multiple items for different output types)
            extendedData: product.dataExtended || [],

            // Common image paths
            heroImage: Utils.resolveHeroImage(product.data?.images?.hero, product.shape),
            coverImage: product.data?.images?.cover?.[0] || '',
            dimensionImages: product.data?.images?.dimensions || [],

            // Other commonly accessed values
            productName: product.data?.name || '',
            productDesc: product.data?.desc || '',
            specSheetUrl: product.data?.spec_sheet_permalink || '',
        };
    },

    //
    // Check if user has made custom product selections.
    // Returns true if: user has added additional products (more than 1),
    // OR primary product's quantity has been manually changed.
    //
    hasCustomProductSelections() {
        if (!this.user.products || this.user.products.length === 0) {
            return false;
        }

        // Show if user has added additional products (more than 1)
        if (this.user.products.length > 1) {
            return true;
        }

        // Show if primary product's quantity has been manually changed
        const primaryProduct = this.getPrimaryProduct;
        if (primaryProduct && primaryProduct.manualUnits !== null && primaryProduct.manualUnits !== undefined) {
            return true;
        }

        return false;
    },

    /*
	------------------------------------------------------------------------
	Step Validations
	------------------------------------------------------------------------
	*/

    //
    // Start step validations
    //
    validateFirstName: function () {
        return Validations.validateFirstName(this);
    },
    validateLastName: function () {
        return Validations.validateLastName(this);
    },
    validateCompany: function () {
        return Validations.validateCompany(this);
    },
    validateEmail: function () {
        return Validations.validateEmail(this);
    },
    stepStartIsValid: function () {
        return Validations.validateStart(this);
    },
    //
    // Project step validations
    //
    validateProjectName: function () {
        return Validations.validateProjectName(this);
    },
    validateRoomName: function () {
        return Validations.validateRoomName(this);
    },
    stepProjectIsValid: function () {
        return Validations.validateProject(this);
    },
    //
    // Room step validations
    //
    validateRoomWidth: function () {
        return Validations.validateRoomWidth(this);
    },
    validateRoomLength: function () {
        return Validations.validateRoomLength(this);
    },
    validateRoomHeight: function () {
        return Validations.validateRoomHeight(this);
    },
    validateRoomUsage: function () {
        return Validations.validateRoomUsage(this);
    },
    validateRoomMaterials: function () {
        return Validations.validateRoomMaterials(this);
    },
    stepRoomIsValid: function () {
        return Validations.validateRoom(this);
    },
    //
    // Product step validations
    //
    validateProductSelected: function () {
        return Validations.validateProductSelected(this);
    },
    validateProductSize: function () {
        return Validations.validateProductSize(this);
    },
    validateProductLumenOutput: function () {
        return Validations.validateProductLumenOutput(this);
    },
    stepProductIsValid: function () {
        return Validations.validateProduct(this);
    },
    //
    // Report step validations
    // Returns true if all products pass size, variant, and lumen output validations.
    // This is a pure getter - no side effects.
    //
    stepReportIsValid: function () {
        if (!this.user.products || this.user.products.length === 0) {
            return true;
        }

        for (const product of this.user.products) {
            if (!Validations.isProductSizeValid(product)) {
                return false;
            }
            if (!Validations.isProductSizeVariantValid(product)) {
                return false;
            }
            if (!Validations.isProductLumenOutputValid(product)) {
                return false;
            }
        }

        return true;
    },

    /*
    ----------------------------------------------------------------------
    Room calculation getters.
    These provide reactive access to room dimension and area calculations
    with automatic unit conversion.
    ----------------------------------------------------------------------
    */
    unitCodeShort: () => (unit) => {
        return unit === 'metric' ? 'mt' : 'ft';
    },
    unitCodeLong: () => (unit) => {
        return unit === 'metric' ? 'meters' : 'feet';
    },
    calcWidth() {
        return (output) => {
            let w = this.user.room.width;
            if (output === 'imperial' && this.user.room.unit === 'metric') {
                w = (w * METERS_TO_FEET).toFixed(2);
            } else if (output === 'metric' && this.user.room.unit === 'imperial') {
                w = (w * FEET_TO_METERS).toFixed(2);
            }
            return w + (output === 'metric' ? 'mt' : 'ft');
        };
    },
    calcLength() {
        return (output) => {
            let l = this.user.room.length;
            if (output === 'imperial' && this.user.room.unit === 'metric') {
                l = (l * METERS_TO_FEET).toFixed(2);
            } else if (output === 'metric' && this.user.room.unit === 'imperial') {
                l = (l * FEET_TO_METERS).toFixed(2);
            }
            return l + (output === 'metric' ? 'mt' : 'ft');
        };
    },
    calcHeight() {
        return (output) => {
            let h = this.user.room.height;
            if (output === 'imperial' && this.user.room.unit === 'metric') {
                h = (h * METERS_TO_FEET).toFixed(2);
            } else if (output === 'metric' && this.user.room.unit === 'imperial') {
                h = (h * FEET_TO_METERS).toFixed(2);
            }
            return h + (output === 'metric' ? 'mt' : 'ft');
        };
    },
    calcFloorArea() {
        return (output) => {
            let w = this.user.room.width;
            let l = this.user.room.length;
            if (output === 'imperial' && this.user.room.unit === 'metric') {
                w = (w * METERS_TO_FEET).toFixed(2);
                l = (l * METERS_TO_FEET).toFixed(2);
            } else if (output === 'metric' && this.user.room.unit === 'imperial') {
                w = (w * FEET_TO_METERS).toFixed(2);
                l = (l * FEET_TO_METERS).toFixed(2);
            }
            return Math.round(w * l).toLocaleString() + (output === 'metric' ? 'mt' : 'ft') + '\u00B2';
        };
    },
    calcWallArea() {
        return (output) => {
            let w = this.user.room.width;
            let l = this.user.room.length;
            let h = this.user.room.height;
            if (output === 'imperial' && this.user.room.unit === 'metric') {
                w = (w * METERS_TO_FEET).toFixed(2);
                l = (l * METERS_TO_FEET).toFixed(2);
                h = (h * METERS_TO_FEET).toFixed(2);
            } else if (output === 'metric' && this.user.room.unit === 'imperial') {
                w = (w * FEET_TO_METERS).toFixed(2);
                l = (l * FEET_TO_METERS).toFixed(2);
                h = (h * FEET_TO_METERS).toFixed(2);
            }
            return Math.round(2 * (w * h) + 2 * (l * h)).toLocaleString() + (output === 'metric' ? 'mt' : 'ft') + '\u00B2';
        };
    },
    calcSurfaceArea() {
        return (output) => {
            let w = this.user.room.width;
            let l = this.user.room.length;
            let h = this.user.room.height;
            if (output === 'imperial' && this.user.room.unit === 'metric') {
                w = (w * METERS_TO_FEET).toFixed(2);
                l = (l * METERS_TO_FEET).toFixed(2);
                h = (h * METERS_TO_FEET).toFixed(2);
            } else if (output === 'metric' && this.user.room.unit === 'imperial') {
                w = (w * FEET_TO_METERS).toFixed(2);
                l = (l * FEET_TO_METERS).toFixed(2);
                h = (h * FEET_TO_METERS).toFixed(2);
            }
            const floorCeilingArea = 2 * (w * l);
            const wallArea = 2 * (h * w) + 2 * (h * l);
            return Math.round(floorCeilingArea + wallArea).toLocaleString() + (output === 'metric' ? 'mt' : 'ft') + '\u00B2';
        };
    },
    calcVolume() {
        return (output) => {
            let w = this.user.room.width;
            let l = this.user.room.length;
            let h = this.user.room.height;
            if (output === 'imperial' && this.user.room.unit === 'metric') {
                w = (w * METERS_TO_FEET).toFixed(2);
                l = (l * METERS_TO_FEET).toFixed(2);
                h = (h * METERS_TO_FEET).toFixed(2);
            } else if (output === 'metric' && this.user.room.unit === 'imperial') {
                w = (w * FEET_TO_METERS).toFixed(2);
                l = (l * FEET_TO_METERS).toFixed(2);
                h = (h * FEET_TO_METERS).toFixed(2);
            }
            return Math.round(w * l * h).toLocaleString() + (output === 'metric' ? 'mt' : 'ft') + '\u00B3';
        };
    },

    /*
    ----------------------------------------------------------------------
    Calculate lumen table data.
    Filters productDataExtended by current product size and variant,
    and returns array of products with calculated total lumen output.
    Used by both the Report component and the RoomCanvas display.

    @return {Array} Array of lumen table data objects.
    ----------------------------------------------------------------------
    */
    calculateLumenTableData() {
        $log.log('[store-getters:calculateLumenTableData] Starting calculation');
        if (!this.user.products || this.user.products.length === 0) {
            $log.log('[store-getters:calculateLumenTableData] No products found in store');
            return [];
        }

        const primaryProduct = this.getPrimaryProduct;
        if (!primaryProduct) {
            $log.log('[store-getters:calculateLumenTableData] Primary product not found');
            return [];
        }

        const extendedData = primaryProduct.dataExtended || [];
        if (!Array.isArray(extendedData) || extendedData.length === 0) {
            $log.log('[store-getters:calculateLumenTableData] primaryProduct.dataExtended is not available or empty - no lumen table will be shown');
            $log.log(`[store-getters:calculateLumenTableData] primaryProduct: ${JSON.stringify({ id: primaryProduct.id, product: primaryProduct.product, size: primaryProduct.size, hasDataExtended: !!primaryProduct.dataExtended }, null, 2)}`);
            return [];
        }

        const selectedLumenOutput = primaryProduct.selectedLumenOutput;
        if (!selectedLumenOutput) {
            $log.log('[store-getters:calculateLumenTableData] No lumen output selected for primary product - no lumen table will be shown');
            return [];
        }

        const selectedOutputData = extendedData.filter((item) => item.output === selectedLumenOutput);
        if (selectedOutputData.length === 0) {
            $log.log(`[store-getters:calculateLumenTableData] Selected lumen output "${selectedLumenOutput}" not found in extendedData - no lumen table will be shown`);
            return [];
        }

        //
        // Get required units from primary product's calculation.
        // Try calculation.unitsUsed first, then manualUnits, then calculatedUnits.
        //
        const requiredUnits = primaryProduct.calculation?.unitsUsed || primaryProduct.manualUnits || primaryProduct.calculation?.calculatedUnits || 0;

        $log.log(`[store-getters:calculateLumenTableData] primaryProductSize: ${primaryProduct.size}`);
        $log.log(`[store-getters:calculateLumenTableData] primaryProductVariant: ${primaryProduct.size_variant || 'none'}`);
        $log.log(`[store-getters:calculateLumenTableData] selectedLumenOutput: ${selectedLumenOutput}`);
        $log.log(`[store-getters:calculateLumenTableData] requiredUnits: ${requiredUnits}`);
        $log.log(`[store-getters:calculateLumenTableData] selectedOutputData count: ${selectedOutputData.length}`);

        const lumenTableData = selectedOutputData.map((item) => ({
            product_output: item.output || '',
            product_lumen_total: item.lumen_total || 0,
            product_qty: requiredUnits,
            sum_lumen_total: (item.lumen_total || 0) * requiredUnits,
        }));

        $log.log(`[store-getters:calculateLumenTableData] lumenTableData: ${JSON.stringify(lumenTableData, null, 4)}`);
        return lumenTableData;
    },

    /*
    ----------------------------------------------------------------------
    Room acoustic calculation result getters.
    Provide formatted access to room baseline and combined calculation values.
    ----------------------------------------------------------------------
    */
    startingT60() {
        return this.roomBaseline?.roomT60 !== undefined ? Number(this.roomBaseline.roomT60).toFixed(2) : '0.00';
    },
    targetT60() {
        return this.user.room.usagetarget !== undefined ? Number(this.user.room.usagetarget).toFixed(2) : '0.00';
    },
    resultingT60() {
        return this.combinedCalculation?.resultingT60 !== undefined ? Number(this.combinedCalculation.resultingT60).toFixed(2) : '0.00';
    },
    startingSabins() {
        return this.roomBaseline?.roomMetricSabins !== undefined ? Math.round(this.roomBaseline.roomMetricSabins) : 0;
    },
    targetSabins() {
        return this.combinedCalculation?.requiredTotalMetricSabins !== undefined ? Math.round(this.combinedCalculation.requiredTotalMetricSabins) : 0;
    },
    totalSabins() {
        return this.combinedCalculation?.totalMetricSabins !== undefined ? Math.round(this.combinedCalculation.totalMetricSabins) : 0;
    },

    // Derived calculations
    productsSabins() {
        return this.totalSabins - this.startingSabins;
    },
    productsT60() {
        const diff = parseFloat(this.startingT60) - parseFloat(this.resultingT60);
        return diff.toFixed(2);
    },

    // Deltas (formatted strings)
    t60Delta() {
        const delta = parseFloat(this.resultingT60) - parseFloat(this.targetT60);
        const sign = delta >= 0 ? '+' : '';
        return `(${sign}${delta.toFixed(2)})`;
    },
    sabinsDelta() {
        const delta = this.totalSabins - this.targetSabins;
        const sign = delta >= 0 ? '+' : '';
        return `(${sign}${delta})`;
    },

    // Product count display text
    productCountText() {
        if (!this.user.products || this.user.products.length === 0) {
            return '0 PRODUCTS';
        }

        let distinctProductCount = 0;
        let totalUnits = 0;

        this.user.products.forEach((product) => {
            distinctProductCount++;
            const quantity = product.manualUnits ?? product.calculation?.unitsUsed ?? product.calculation?.calculatedUnits ?? 1;
            totalUnits += quantity;
        });

        if (distinctProductCount === 0) {
            return '0 PRODUCTS';
        }

        const productText = distinctProductCount === 1 ? '1 PRODUCT' : `${distinctProductCount} PRODUCTS`;
        const unitsText = totalUnits === 1 ? '1 UNIT' : `${totalUnits} UNITS`;

        return `${productText} (${unitsText})`;
    },

    // Total lumens across all products
    totalLumens() {
        let total = 0;

        if (!this.user.products || !Array.isArray(this.user.products)) {
            return Math.round(total).toLocaleString();
        }

        this.user.products.forEach((product) => {
            if (!product.selectedLumenOutput || !product.dataExtended || product.dataExtended.length === 0) {
                return;
            }

            const selectedOutput = product.dataExtended.find((item) => item.output === product.selectedLumenOutput);

            if (selectedOutput && selectedOutput.lumen_total) {
                const quantity = product.manualUnits ?? product.calculation?.unitsUsed ?? 1;
                total += selectedOutput.lumen_total * quantity;
            }
        });

        return Math.round(total).toLocaleString();
    },

    // Target validation (T60 lower is better)
    isTargetMet() {
        const resulting = parseFloat(this.resultingT60);
        const target = parseFloat(this.targetT60);

        if (isNaN(resulting) || isNaN(target)) return false;

        return resulting <= target;
    },

    /*
	------------------------------------------------------------------------
	Validation getters for the REPORT step.
	These provide reactive access to product validation functions.
	Used by both the Report component and the ResultsWidget display.
	These are used to check if a specific product's lumen output and size variant are valid.
	------------------------------------------------------------------------
	*/

    //
    // Check if a specific product's lumen output is valid.
    // Returns a function: store.isProductLumenOutputValid(productId) → boolean
    //
    isProductLumenOutputValid() {
        return (productId) => {
            const product = this.user.products.find((p) => p.id === productId);
            if (!product) return true;
            return Validations.isProductLumenOutputValid(product);
        };
    },

    //
    // Check if a specific product's size variant is valid.
    // Returns a function: store.isProductSizeVariantValid(productId) → boolean
    //
    isProductSizeVariantValid() {
        return (productId) => {
            const product = this.user.products.find((p) => p.id === productId);
            if (!product) return true;
            return Validations.isProductSizeVariantValid(product);
        };
    },

    //
    // Get all invalid products with their invalid fields.
    // Returns array: [{ product, productId, productName, invalidFields }, ...]
    // Use for displaying "fix these issues" UI.
    //
    invalidProducts() {
        const invalid = [];

        for (const product of this.user.products) {
            const invalidFields = [];

            if (!Validations.isProductSizeValid(product)) {
                invalidFields.push('size');
            }
            if (!Validations.isProductSizeVariantValid(product)) {
                invalidFields.push('sizeVariant');
            }
            if (!Validations.isProductLumenOutputValid(product)) {
                invalidFields.push('lumenOutput');
            }
            // Add more field checks here as needed

            if (invalidFields.length > 0) {
                invalid.push({
                    product: product,
                    productId: product.id,
                    productName: product.data?.name || 'Product',
                    invalidFields: invalidFields,
                });
            }
        }

        return invalid;
    },

    //
    // Check if there are any invalid products.
    // Shorthand for: store.invalidProducts.length > 0
    //
    hasInvalidProducts() {
        return this.invalidProducts.length > 0;
    },
};
